import {
  GetLatestMentorReportStatusDTO,
  GetTotalPaymentByMonthDTO,
  MentorActivityReportDTO,
  ReportsFinancialStatsDTO,
  SearchReportsDTO,
} from '@shared/clientTypes/DTOs/ReportsDTO';
import { ApiDescription, ApiData, RequestsMethod, Report, ExchangeRate } from '../common';

export const generateCSVReport: ApiDescription = {
  endpoint: '/reports/csv',
  method: RequestsMethod.Post,
};

export type GenerateCSVReport = ApiData<
  {
    date: string;
    reportIds: string[];
  },
  {
    csvData: any;
  }
>;

export const getLatestMentorReportStatus: ApiDescription = {
  endpoint: '/report/latest-status',
  method: RequestsMethod.Get,
};

export type GetLatestMentorReportStatus = ApiData<never, GetLatestMentorReportStatusDTO>;

export const createMonthlyReports: ApiDescription = {
  endpoint: '/reports/monthly',
  method: RequestsMethod.Post,
};

export type CreateMonthlyReports = ApiData<{ date: string }, unknown>;

export const deleteCurrentMonthReports: ApiDescription = {
  endpoint: '/reports/monthly',
  method: RequestsMethod.Delete,
};

export type DeleteCurrentMonthReports = ApiData<unknown, unknown>;

export const getNoOfSessionsByMonth: ApiDescription = {
  endpoint: '/report/monthly/no-of-sessions',
  method: RequestsMethod.Get,
};

export type GetNoOfSessionsByMonth = ApiData<
  {
    date: string;
  },
  {
    noOfSessions: number;
  }
>;

export const getNoOfActiveMentorsByMonth: ApiDescription = {
  endpoint: '/report/monthly/no-of-active-mentors',
  method: RequestsMethod.Get,
};

export type GetNoOfActiveMentorsByMonth = ApiData<
  {
    date: string;
  },
  {
    noOfActiveMentors: number;
  }
>;

export const getTotalPaymentByMonth: ApiDescription = {
  endpoint: '/report/monthly/total-payment',
  method: RequestsMethod.Get,
};

export type GetTotalPaymentByMonth = ApiData<
  {
    date: string;
  },
  GetTotalPaymentByMonthDTO
>;

export const getRatesByMonth: ApiDescription = {
  endpoint: '/reports/monthly/rates',
  method: RequestsMethod.Get,
};
export type GetRatesByMonth = ApiData<{ date: string }, ExchangeRate[]>;

export const getMentorActivityReport: ApiDescription = {
  endpoint: '/reports/mentor-activity',
  method: RequestsMethod.Get,
};

export type GetMentorActivityReport = ApiData<
  { mentorId: string; rangeStart?: string; rangeEnd?: string },
  MentorActivityReportDTO
>;

export const downloadMentorActivityArchive: ApiDescription = {
  endpoint: '/reports/bulk/download-mentor-archive',
  method: RequestsMethod.Post,
};

export type DownloadMentorActivityArchive = ApiData<
  { mentorId: string; rangeStart?: string; rangeEnd?: string },
  unknown
>;

export const getReportsFinancialStats: ApiDescription = {
  endpoint: '/reports/financial-stats',
  method: RequestsMethod.Get,
};
export type GetReportsFinancialStats = ApiData<{ date: string }, ReportsFinancialStatsDTO>;

export const getMultipleReports: ApiDescription = {
  endpoint: '/report/multiple',
  method: RequestsMethod.Post,
};
export type GetMultipleReports = ApiData<{ reportIds: string[] }, Report[]>;

export const searchReports: ApiDescription = {
  endpoint: '/reports/search',
  method: RequestsMethod.Get,
};
export type SearchReports = ApiData<
  {
    date: string;
    page: number;
    limit?: number;
    search?: string;
    status?: number;
    sortType?: string;
    sortValue?: string;
    isTestUser?: boolean;
  },
  SearchReportsDTO
>;

export const uploadInvoiceByMentor: ApiDescription = {
  endpoint: '/report/invoice/:id/upload-invoice',
  method: RequestsMethod.Post,
};

export type UploadInvoiceByMentor = ApiData<{ invoice: any; invoiceNo: string }, Report>;

export const markBulkReportsReadyForPayment: ApiDescription = {
  endpoint: '/report/bulk/mark-ready-for-payment',
  method: RequestsMethod.Post,
};

export type MarkBulkReportsReadyForPayment = ApiData<
  {
    reportIds: string[];
  },
  Report[]
>;

export const markBulkReportsAsPaid: ApiDescription = {
  endpoint: '/report/bulk/mark-paid',
  method: RequestsMethod.Post,
};

export type MarkBulkReportsAsPaid = ApiData<
  {
    reportIds: string[];
  },
  Report[]
>;

export const getMentorReportActivityHistory: ApiDescription = {
  endpoint: '/reports/activity-history',
  method: RequestsMethod.Get,
};

export type GetMentorReportActivityHistory = ApiData<{ id: string; date: string }, Report[]>;

export const getReport: ApiDescription = {
  endpoint: '/report/:id',
  method: RequestsMethod.Get,
};

export type GetReport = ApiData<never, Report>;

export const createReport: ApiDescription = {
  endpoint: '/report/:id',
  method: RequestsMethod.Post,
};

export type CreateReport = ApiData<{ date: string }, Report>;

export const deleteReport: ApiDescription = {
  endpoint: '/report/:id',
  method: RequestsMethod.Delete,
};

export type DeleteReport = ApiData<never, Report>;

export const regenerateReport: ApiDescription = {
  endpoint: '/report/:id/regenerate',
  method: RequestsMethod.Post,
};

export type RegenerateReport = ApiData<unknown, Report>;

export const revertReportToAwaitingInvoice: ApiDescription = {
  endpoint: '/report/:id/revert-to-awaiting-invoice',
  method: RequestsMethod.Post,
};

export type RevertReportToAwaitingInvoice = ApiData<
  unknown,
  { report: Report; sessionsReverted: number; extraServicesReverted: number }
>;

export const uploadInvoiceByOperations: ApiDescription = {
  endpoint: '/report/:id/upload-invoice-operations',
  method: RequestsMethod.Post,
};

export type UploadInvoiceByOperations = ApiData<{ invoice: any; invoiceNo: string }, Report>;

export const markReportAsPaid: ApiDescription = {
  endpoint: '/report/:id/mark-paid',
  method: RequestsMethod.Post,
};

export type MarkReportAsPaid = ApiData<unknown, Report>;

export const downloadInvoice: ApiDescription = {
  endpoint: '/report/:id/download-invoice',
  method: RequestsMethod.Get,
};

export type DownloadInvoice = ApiData<never, unknown>;

export const downloadActivityReport: ApiDescription = {
  endpoint: '/report/:id/download-activity-report',
  method: RequestsMethod.Get,
};

export type DownloadActivityReport = ApiData<never, unknown>;

export const downloadBulkInvoices: ApiDescription = {
  endpoint: '/reports/bulk/download-invoices',
  method: RequestsMethod.Post,
};

export type BulkDownloadFilters = {
  date: string;
  mentor?: string;
  status?: number | null;
  isTestUser?: boolean | null;
};

export type DownloadBulkInvoices = ApiData<
  BulkDownloadFilters & { invoiceIds?: string[] },
  unknown
>;

export const downloadBulkActivityReports: ApiDescription = {
  endpoint: '/reports/bulk/download-activity-reports',
  method: RequestsMethod.Post,
};

export type DownloadBulkActivityReports = ApiData<
  BulkDownloadFilters & { reportIds?: string[] },
  unknown
>;

export const generatePFDsForAllReports: ApiDescription = {
  endpoint: '/reports/bulk/generate-pdfs',
  method: RequestsMethod.Patch,
};

export type GeneratePFDsForAllReports = ApiData<never, unknown>;

export const generatePFDsForMentorReports: ApiDescription = {
  endpoint: '/reports/generate-pdfs/:id',
  method: RequestsMethod.Patch,
};

export type GeneratePFDsForMentorReports = ApiData<never, unknown>;

export const clearActivityReportIds: ApiDescription = {
  endpoint: '/reports/clear-activity-report-ids',
  method: RequestsMethod.Patch,
};

export type ClearActivityReportIds = ApiData<never, unknown>;
export const checkMentorLastMonthMeetingAndExtraServices: ApiDescription = {
  endpoint: '/reports/check-mentor-last-month-meeting-and-extra-services/:id',
  method: RequestsMethod.Get,
};

export type CheckMentorLastMonthMeetingAndExtraServices = ApiData<unknown, boolean>;

export const reportStatusHandler: ApiDescription = {
  endpoint: '/reports/report-status-handler',
  method: RequestsMethod.Patch,
};

export type ReportStatusHandler = ApiData<
  { reportId: string; status: string; reason?: string },
  Report
>;

export const generateAcceptedInvoicesCSVData: ApiDescription = {
  endpoint: '/reports/csv/accepted-invoices',
  method: RequestsMethod.Get,
};

export type GenerateAcceptedInvoicesCSVData = ApiData<never, any>;

export const generateAwaitingInvoiceCSVData: ApiDescription = {
  endpoint: '/reports/csv/awaiting-invoice',
  method: RequestsMethod.Get,
};

export type GenerateAwaitingInvoiceCSVData = ApiData<{ date: string }, { csvData: string }>;

export const generateMentorDataCSVData: ApiDescription = {
  endpoint: '/reports/csv/mentor-data',
  method: RequestsMethod.Get,
};

export type GenerateMentorDataCSVData = ApiData<
  {
    date: string;
    search?: string;
    status?: number;
    sortType?: string;
    sortValue?: string;
    isTestUser?: boolean;
  },
  { csvData: string }
>;

export const sendAwaitingInvoiceReminders: ApiDescription = {
  endpoint: '/reports/awaiting-invoice/reminders',
  method: RequestsMethod.Post,
};

export type SendAwaitingInvoiceReminders = ApiData<
  { date: string },
  { totalMentors: number; sent: number; failed: number }
>;

export const electronicInvoiceStatusHandler: ApiDescription = {
  endpoint: '/reports/e-invoice-status-handler',
  method: RequestsMethod.Patch,
};

export type ElectronicInvoiceStatusHandler = ApiData<
  { reportId: string; status: string; reason?: string },
  Report
>;

export const getStuckReports: ApiDescription = {
  endpoint: '/reports/stuck',
  method: RequestsMethod.Get,
};

export type StuckReport = {
  _id: string;
  mentorName: string;
  month: string;
  year: string;
  status: number;
  updatedAt: string;
  errorMessage?: string;
  isEmpty: boolean;
};

export type GetStuckReports = ApiData<unknown, { reports: StuckReport[] }>;

export const requeueReportPdf: ApiDescription = {
  endpoint: '/report/:id/requeue-pdf',
  method: RequestsMethod.Post,
};

export type RequeueReportPdf = ApiData<unknown, { queued: boolean }>;

export type ExchangeRateRowDTO = {
  date: string;
  EUR?: number;
  USD?: number;
  GBP?: number;
};

export const listExchangeRates: ApiDescription = {
  endpoint: '/exchange-rates',
  method: RequestsMethod.Get,
};

export type ListExchangeRates = ApiData<
  { year?: number; month?: number },
  { rows: ExchangeRateRowDTO[]; latestDate?: string; years: number[] }
>;

export const syncExchangeRates: ApiDescription = {
  endpoint: '/exchange-rates/sync',
  method: RequestsMethod.Post,
};

export type SyncExchangeRates = ApiData<unknown, { added: number; latestDate?: string }>;

export const downloadOperationsMonthlyExport: ApiDescription = {
  endpoint: '/reports/operations-export',
  method: RequestsMethod.Get,
};

export type DownloadOperationsMonthlyExport = ApiData<
  { month: string },
  {
    month: string;
    meetingsCsv: string;
    extraServicesCsv: string;
    meetingsCount: number;
    extraServicesCount: number;
  }
>;

export const generateUnbilledSessionsCSV: ApiDescription = {
  endpoint: '/reports/csv/unbilled-sessions',
  method: RequestsMethod.Get,
};

export type GenerateUnbilledSessionsCSV = ApiData<never, { csvData: string; count: number }>;

export const generateUnpayableSessionsExport: ApiDescription = {
  endpoint: '/reports/csv/unpayable-sessions',
  method: RequestsMethod.Get,
};

export type GenerateUnpayableSessionsExport = ApiData<
  never,
  { sessionsCsv: string; perMentorCsv: string; count: number; mentorCount: number }
>;
